import { triadSuperFetch } from 'utils/superFetch';
import { useEffect, useState } from 'react';
import { LOCAL_TRIADMS_API_BETA_VERSION } from 'configs';
import { BACKEND_ENDPOINTS, SITE_TYPE } from 'consts';
import { PublicEnvironmentVariables } from 'types';
import {
  deleteCookie,
  getCookie,
  setCookie,
  TRIAD_USER_SESSION_ID,
} from 'utils/analyticsHelpers';
import { APIError } from 'utils/errors/APIError';
import styles from '../ConfigModal.module.css';
import { availableWPInstances } from '../../controls/availableWordpressEnvironments';
import { DropdownSelect } from '../common/DropdownSelect';

function groupNameTransform(siteType: SITE_TYPE): string {
  if (siteType === SITE_TYPE.MICRO_SITE) {
    return 'Micro Sites';
  }

  if (siteType === SITE_TYPE.CLICK_PORTAL) {
    return 'Click Portals';
  }

  return 'Micro Portals';
}

interface AvailableSite {
  primaryFrontendDomain: string;
  schoolCode: string;
  siteType: SITE_TYPE;
  name: string;
  theme: string;
}

export function EnvironmentSection({
  environmentVariables,
}: {
  environmentVariables: PublicEnvironmentVariables;
}): React.JSX.Element {
  const [availableSites, setAvailableSites] = useState<AvailableSite[]>([]);
  const { WPDomain, triadBackendDomain, developmentSiteUrl } =
    environmentVariables;
  const isLocalDev = environmentVariables.configEnv === 'development';

  const [currentSelections, setCurrentSelections] = useState({
    WPDomain,
    triadBackendDomain: getCookie('triadBackendDomain') || triadBackendDomain,
    developmentSiteUrl,
  });

  async function loadAvailableSites(
    wordpressInstance: string
  ): Promise<[APIError | null, AvailableSite[]]> {
    const [apiError, response] = await triadSuperFetch<AvailableSite[]>(
      `https://${wordpressInstance}/wp-json/triad/v1/sites`,
      {
        cache: {
          type: 'sessionStorage',
          key: `available-sites-${wordpressInstance}`,
        },
        retry: {
          maxAttempts: 5,
          waitTimeBetweenFails: 2000,
        },
        method: 'GET',
        timeout: 3000,
      }
    );

    if (apiError) {
      alert(
        `Failed to Load Sites: ${apiError.message}. endpoint: ${apiError.endpoint}`
      );
      return [apiError, []];
    }

    setAvailableSites(response);
    return [null, response];
  }

  useEffect(() => {
    if (!currentSelections.developmentSiteUrl) {
      setCurrentSelections({
        ...currentSelections,
        developmentSiteUrl: window.location.host,
      });
    }

    if (currentSelections.WPDomain) {
      loadAvailableSites(currentSelections.WPDomain);
    }
  }, []);

  const [isUpdating, setIsUpdating] = useState(false);
  const [isResetting, setIsResetting] = useState(false);
  const [isDirty, setIsDirty] = useState(false);

  useEffect(() => {
    setIsDirty(
      currentSelections.WPDomain !== WPDomain ||
        currentSelections.triadBackendDomain !== triadBackendDomain ||
        currentSelections.developmentSiteUrl !== developmentSiteUrl
    );
  }, [currentSelections, WPDomain, triadBackendDomain, developmentSiteUrl]);

  async function handleUpdate(): Promise<void> {
    setCookie('triadBackendDomain', currentSelections.triadBackendDomain, 30);

    if (!isLocalDev) {
      deleteCookie(TRIAD_USER_SESSION_ID);

      if (window.location.pathname === '/500') {
        window.location.href = '/';
      } else {
        window.location.reload();
      }

      return;
    }

    try {
      setIsUpdating(true);
      await triadSuperFetch('/api/testing/update-dev-site', {
        method: 'POST',
        timeout: 7000,
        body: {
          ...currentSelections,
          theme: availableSites.find(
            (site) =>
              site.primaryFrontendDomain ===
              currentSelections.developmentSiteUrl
          )?.theme,
        },
      });

      // Force page reload after clearing
      deleteCookie(TRIAD_USER_SESSION_ID);
      if (window.location.pathname === '/500') {
        window.location.href = '/';
      } else {
        window.location.reload();
      }
    } catch (error) {
      // eslint-disable-next-line no-alert
      alert((error as Error).message);
      setIsUpdating(false);
    }
  }

  async function handleReset(): Promise<void> {
    try {
      setIsResetting(true);
      await triadSuperFetch('/api/testing/update-dev-site', {
        method: 'POST',
        timeout: 3000,
        body: {
          reset: true,
        },
      });

      // Clear all storage and cookies
      localStorage.clear();
      sessionStorage.clear();

      // Clear all cookies by setting expired date
      document.cookie.split(';').forEach((cookie) => {
        document.cookie = cookie
          .replace(/^ +/, '')
          .replace(/=.*/, `=;expires=${new Date().toUTCString()};path=/`);
      });

      // Force page reload after clearing
      window.location.reload();
    } catch (error) {
      // eslint-disable-next-line no-alert
      alert((error as Error).message);
      setIsResetting(false);
    }
  }

  return (
    <section className={styles.section}>
      <div className={styles.inputGroup}>
        {isLocalDev && (
          <DropdownSelect
            id="wpInstance"
            label="WordPress Instance"
            disabled={!isLocalDev}
            value={currentSelections.WPDomain}
            options={availableWPInstances}
            onChange={async (value) => {
              const [apiError, response] = await loadAvailableSites(value);
              if (apiError) {
                alert(
                  `Failed to Load Sites: ${apiError.message}. endpoint: ${apiError.endpoint}`
                );
              }

              setAvailableSites(response);

              const updatedDevelopmentSiteUrl = response.find(
                (site) =>
                  site.primaryFrontendDomain.split('.').splice(1).join('.') ===
                  currentSelections.developmentSiteUrl
                    .split('.')
                    .splice(1)
                    .join('.')
              )?.primaryFrontendDomain;

              if (updatedDevelopmentSiteUrl) {
                setCurrentSelections({
                  ...currentSelections,
                  developmentSiteUrl: updatedDevelopmentSiteUrl,
                  WPDomain: value,
                });
              } else {
                alert(
                  `Failed to swap site from: ${currentSelections.developmentSiteUrl}`
                );
              }
            }}
          />
        )}

        {currentSelections.developmentSiteUrl && (
          <p>{currentSelections.developmentSiteUrl}</p>
        )}

        {isLocalDev && (
          <DropdownSelect
            id="wpSite"
            label="WordPress Site"
            disabled={!isLocalDev}
            value={currentSelections.developmentSiteUrl}
            onChange={(value) =>
              setCurrentSelections({
                ...currentSelections,
                developmentSiteUrl: value,
              })
            }
            options={Object.values(SITE_TYPE).map((siteType) => ({
              groupName: groupNameTransform(siteType),
              options: availableSites
                .filter((site) => site.siteType === siteType)
                .map((site) => {
                  return {
                    value: site.primaryFrontendDomain,
                    label: site.name,
                  };
                }),
            }))}
          />
        )}

        <DropdownSelect
          id="backendEnv"
          label="Backend Environment"
          value={currentSelections.triadBackendDomain}
          options={Object.keys(BACKEND_ENDPOINTS)
            .filter((endpoint) => endpoint !== 'PROD')
            .map((endpoint) => {
              const endpointValue = `${
                BACKEND_ENDPOINTS[endpoint as keyof typeof BACKEND_ENDPOINTS]
              }${LOCAL_TRIADMS_API_BETA_VERSION}`;

              return {
                value: endpointValue,
                label: `${endpoint} (${endpointValue})`,
              };
            })}
          onChange={(value) =>
            setCurrentSelections({
              ...currentSelections,
              triadBackendDomain: value,
            })
          }
        />
      </div>
      <div className={styles.buttonContainer}>
        {isLocalDev && (
          <button
            type="button"
            className={styles.resetButton}
            onClick={handleReset}
            disabled={isResetting || isUpdating}
          >
            {isResetting ? 'Resetting...' : 'Reset'}
          </button>
        )}
        <button
          type="button"
          className={styles.updateButton}
          onClick={handleUpdate}
          disabled={!isDirty || isResetting || isUpdating}
        >
          {isUpdating ? 'Updating...' : 'Update'}
        </button>
      </div>
    </section>
  );
}
